import React, { useEffect, useMemo, useRef } from 'react';
import { styled } from '@mui/material/styles';
import { Box, Paper, Tab, Tabs } from '@mui/material';

import { useCommonTranslation } from '../../../hooks/useCommonTranslation';
import { useAppDispatch } from '../../../hooks/useAppDispatch';
import { useAppSelector } from '../../../hooks/useAppSelector';

import { Client } from '../../../data/models/client.model';

import {
  fetchUserRolesAsync,
  selectUserRoles,
} from '../../../data/security.slice';
import { selectUserCategoryTypes } from '../../../data/lookup.slice';
import * as clientSlice from '../../../data/client.slice';
import * as accountingSlice from '../../../data/accounting.slice';
import * as lookupSlice from '../../../data/lookup.slice';

import TabPanel from '../../TabPanel';
import ClientLocations from './locations/ClientLocations';
import ClientPreferences from './preferences/ClientPreferences';
import ClientProfileTab from './details/ClientProfileTab';
import ClientProgramsTab from './details/ClientProgramsTab';
import ClientPricingTab from './details/ClientPricingTab';

const PREFIX = 'ClientDetails';

const classes = {
  container: `${PREFIX}-container`,
  tab: `${PREFIX}-tab`,
};

const StyledBox = styled(Paper)(({ theme }) => ({
  [`&.${classes.container}`]: {
    display: 'flex',
    flexDirection: 'column',
    flexGrow: 1,
    margin: theme.spacing(2),
  },
  [`& .${classes.tab}`]: {
    display: 'flex',
    alignItems: 'flex-start',
    padding: theme.spacing(2),
    gap: theme.spacing(2),
    flexWrap: 'wrap',
  },
}));

interface ClientDetailsProps {
  client?: Client;
  readonly: boolean;
}

/**
 * Tab shell for a client's details. Each tab owns its own UI state and
 * dialogs, so state changes in one tab (e.g. drag/drop on Programs) don't
 * re-render the others. This component keeps the per-client data fetches,
 * admin paging, and the service plan selection.
 */
export default function ClientDetails({
  client,
  readonly,
}: ClientDetailsProps) {
  const t = useCommonTranslation();
  const dispatch = useAppDispatch();
  const clientBC = useRef(client?.clientBC);

  const preferences = useAppSelector(clientSlice.selectClientSettings);
  const userCategoryTypes = useAppSelector(selectUserCategoryTypes);
  const userRoles = useAppSelector(selectUserRoles);

  const [tabIndex, setTabIndex] = React.useState(0);
  const [adminUserPage, setAdminUserPage] = React.useState(1);
  const [servicePlanSelections, setServicePlanSelections] = React.useState<{
    [id: string]: boolean;
  }>({});

  const userCategoryTypeIDs = useMemo(() => {
    return userCategoryTypes
      .filter((type) => type.userCategoryTypeBC === 'CLIENT')
      .map((type) => type.userCategoryTypeID);
  }, [userCategoryTypes]);

  const userRoleIDs = useMemo(() => {
    return userRoles
      .filter(
        (userRole) =>
          userRole.userRoleBC === 'clientportal.client.admin' &&
          userRole.userRoleID !== undefined,
      )
      .map((userRole) => userRole.userRoleID)
      .filter((id): id is number => id !== undefined);
  }, [userRoles]);

  const handleTabChange = (_evt: React.ChangeEvent<{}>, newValue: number) => {
    setTabIndex(newValue);
  };

  useEffect(() => {
    if (client?.clientBC && clientBC.current !== client?.clientBC) {
      clientBC.current = client?.clientBC;
      dispatch(clientSlice.fetchClientAddressesAsync(client.clientBC));
      dispatch(clientSlice.fetchClientContactsAsync(client.clientBC));
      dispatch(clientSlice.fetchClientPurposeCodesAsync(client.clientBC));
      dispatch(clientSlice.fetchClientServiceAreasAsync(client.clientBC));
      dispatch(clientSlice.fetchTransportationBenefitsAsync(client.clientBC));
      dispatch(accountingSlice.fetchPricingModelsAsync());
      dispatch(clientSlice.fetchClientServicePlansAsync(client.clientBC));
      dispatch(clientSlice.fetchClientProgramsAsync(client.clientBC));
      dispatch(clientSlice.fetchClientSettingsAsync(client.clientBC));
      dispatch(lookupSlice.fetchLookupItemsAsync('chargeType'));
      dispatch(lookupSlice.fetchLookupItemsAsync('pricingCalculationGroup'));
      dispatch(accountingSlice.fetchPricingFunctionsAsync());
      dispatch(clientSlice.fetchTransportationBenefitModelsAsync());
      dispatch(clientSlice.fetchTransportationBenefitFunctionsAsync());
      dispatch(lookupSlice.fetchLookupItemsAsync('benefitPurpose'));
      dispatch(lookupSlice.fetchLookupItemsAsync('lineOfBusiness'));
    }
  }, [client?.clientBC]);

  useEffect(() => {
    if (userCategoryTypeIDs.length > 0) {
      dispatch(fetchUserRolesAsync({ categoryId: userCategoryTypeIDs[0] }));
    }
  }, [userCategoryTypeIDs]);

  useEffect(() => {
    if (userRoleIDs.length > 0 && client?.clientBC) {
      dispatch(
        clientSlice.fetchClientAdminsAsync(
          client.clientBC,
          adminUserPage,
          5,
          userRoleIDs,
        ),
      );
    }
  }, [userRoleIDs, client?.clientBC, adminUserPage]);

  useEffect(() => {
    dispatch(lookupSlice.fetchLookupItemsAsync('userCategoryType'));
  }, []);

  return (
    <StyledBox elevation={0} className={classes.container}>
      <Tabs
        indicatorColor="primary"
        textColor="primary"
        value={tabIndex}
        onChange={handleTabChange}
        variant="fullWidth">
        <Tab label={t('client.profile')} data-testid="tab-one" />
        <Tab label={t('client.programs')} data-testid="tab-two" />
        <Tab label={t('client.pricing')} data-testid="tab-three" />
        {/* <Tab label={t('client.benefits')} data-testid="tab-four" /> */}
        <Tab label={t('client.locations')} data-testid="tab-four" />
        {/* The Preferences tab is removed for now */}
        {/* <Tab label={t('client.preferences')} data-testid="tab-six" /> */}
      </Tabs>
      <TabPanel value={tabIndex} index={0} keepMounted>
        <Box className={classes.tab}>
          <ClientProfileTab
            client={client}
            readonly={readonly}
            userRoleIDs={userRoleIDs}
            userCategoryTypeIDs={userCategoryTypeIDs}
            onAdminPageChange={setAdminUserPage}
          />
        </Box>
      </TabPanel>
      <TabPanel value={tabIndex} index={1} keepMounted>
        <Box className={classes.tab}>
          {!readonly && (
            <ClientProgramsTab
              client={client}
              servicePlanSelections={servicePlanSelections}
              setServicePlanSelections={setServicePlanSelections}
            />
          )}
        </Box>
      </TabPanel>
      <TabPanel value={tabIndex} index={2}>
        <Box className={classes.tab}>
          {client && <ClientPricingTab clientBC={client.clientBC} />}
        </Box>
      </TabPanel>
      <TabPanel value={tabIndex} index={3}>
        <Box className={classes.tab}>
          {client && <ClientLocations client={client} />}
        </Box>
      </TabPanel>
      <TabPanel value={tabIndex} index={5}>
        <Box className={classes.tab}>
          {client && (
            <ClientPreferences
              clientBC={client.clientBC}
              preferences={preferences}
            />
          )}
        </Box>
      </TabPanel>
    </StyledBox>
  );
}
